import { SyntheticEvent } from 'react'
import { Box } from '@mui/material'
import { useSearchParams } from 'react-router-dom'

import { ContentHead } from '../../../components/common/ContentHead'
import DateRangeFilter from '../../../components/common/DateRangeFilter'
import { a11yProps, TabPanel } from '../../../components/common/TabPanel'
import { QUERY_PARAMS } from '../../../constants'
import { TabItem, TabsContainer } from '../../../theme/styleComponents'
import AccountingTab from './AccountingTab'
import BillingTab from './BillingTab'
import MattersTab from './MattersTab'
import { getDefaultRange, RANGE_DATE_FORMAT } from './constants'

/**
 * Firm-wide metrics for admins and accounting. Deliberately not a landing page — every tab runs
 * aggregate queries — and only the open tab's query runs, since TabPanel mounts the active tab only.
 */
const FirmDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const defaultRange = getDefaultRange()

  // The range lives in the URL so it survives a refresh and is shared by every tab
  const startDate = searchParams.get(QUERY_PARAMS.FROM) || defaultRange.startDate
  const endDate = searchParams.get(QUERY_PARAMS.TO) || defaultRange.endDate

  const tabs = [
    { key: 'matters', label: 'Matters', content: <MattersTab startDate={startDate} endDate={endDate} /> },
    { key: 'billing', label: 'Billing', content: <BillingTab startDate={startDate} endDate={endDate} /> },
    // { key: 'accounting', label: 'Accounting', content: <AccountingTab startDate={startDate} endDate={endDate} /> }
  ]
  const activeTab = Math.min(parseInt(searchParams.get(QUERY_PARAMS.ACTIVE_TAB) || '') || 0, tabs.length - 1)

  const updateParams = (updates: Partial<Record<QUERY_PARAMS, string>>) => {
    const next = new URLSearchParams(searchParams)
    Object.entries(updates).forEach(([key, value]) => value && next.set(key, value))
    setSearchParams(next, { replace: true })
  }

  const handleTabChange = (_event: SyntheticEvent, tab: number) =>
    updateParams({ [QUERY_PARAMS.ACTIVE_TAB]: String(tab) })

  return (
    <Box>
      <ContentHead
        title="Firm Dashboard"
        customButton={() => (
          <DateRangeFilter
            fromDate={startDate}
            toDate={endDate}
            onFromDateChange={date => updateParams({ [QUERY_PARAMS.FROM]: date })}
            onToDateChange={date => updateParams({ [QUERY_PARAMS.TO]: date })}
            filterDateFormat={RANGE_DATE_FORMAT}
            allowSameDate
          />
        )}
      />

      <TabsContainer
        value={activeTab}
        onChange={handleTabChange}
        aria-label="firm dashboard tabs"
        TabIndicatorProps={{ sx: { background: 'darkRed' } }}
        sx={{ paddingBlock: '20px' }}
      >
        {tabs.map((tab, index) => (
          <TabItem key={tab.key} label={tab.label} {...a11yProps(index, 'firmDashboard')} />
        ))}
      </TabsContainer>

      {tabs.map((tab, index) => (
        <TabPanel key={tab.key} value={activeTab} index={index} pb="24px">
          {tab.content}
        </TabPanel>
      ))}
    </Box>
  )
}

export default FirmDashboard
